import { ChevronRightIcon, CreditCardIcon, SparklesIcon, WalletIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { ChatAgentItemLayout } from './chat-agent-item-layout.atom'
import type { ChatUserInteraction, UserInteractionClickHandler } from './chat-message.interface'

export interface ChatUserInteractionItemProps {
  interaction: ChatUserInteraction
  agentName?: string
  isPinned?: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: UserInteractionClickHandler
  className?: string
}

const interactionIconMap: Record<
  ChatUserInteraction['interactionType'],
  React.ComponentType<{ className?: string }>
> = {
  'add-credit-card': CreditCardIcon,
  'upgrade-plan': SparklesIcon,
  'top-up-balance': WalletIcon,
}

const interactionDescriptionMap: Record<ChatUserInteraction['interactionType'], string> = {
  'add-credit-card': 'Add a credit card to enable auto top-ups and premium features.',
  'upgrade-plan': 'Unlock more features and higher limits with our premium plan.',
  'top-up-balance': 'Your balance has run out. Top up to continue sending messages.',
}

const ChatUserInteractionItemBase: React.FC<ChatUserInteractionItemProps> = ({
  interaction,
  agentName,
  isPinned = false,
  isHighlighted = false,
  onPin,
  onClick,
  className,
}): ReactNode => {
  const { title, interactionType } = interaction
  const Icon = interactionIconMap[interactionType]
  const description = interactionDescriptionMap[interactionType]
  const handleClick = onClick ? () => onClick(interaction) : undefined

  return (
    <ChatAgentItemLayout
      itemId={interaction.id}
      timestamp={interaction.timestamp}
      agentName={agentName}
      isPinned={isPinned}
      isHighlighted={isHighlighted}
      onPin={onPin}
      onClick={handleClick}
      wrapperClassName="chat-user-interaction"
      cardClassName={cn('chat-user-interaction-card user-interaction-shimmer flex-col gap-2')}
      className={className}
    >
      <div className="flex items-center gap-3">
        <div className="flex size-8 flex-shrink-0 items-center justify-center rounded-md bg-white/20">
          <Icon className="size-4 text-white" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{title}</span>
        </div>

        {onClick && (
          <ChevronRightIcon className="size-4 flex-shrink-0 text-white/50 transition-colors group-hover:text-white" />
        )}
      </div>

      {description && <p className="text-xs leading-relaxed text-white/80">{description}</p>}
    </ChatAgentItemLayout>
  )
}

export const ChatUserInteractionItem = memo(ChatUserInteractionItemBase)

ChatUserInteractionItem.displayName = 'ChatUserInteractionItem'
